import { useState, useEffect, useCallback } from 'react'
import type { Square, Color } from '../chessEngine'
import { getValidMoveSquares, applyMove, sideToMove, parseUci } from '../chessEngine'
import type { ChessDifficulty } from '../chessData'
import type { SquareHighlight } from '../ChessBoard'
import ChessBoard from '../ChessBoard'
import { chess as chessApi } from '../../../api/chess'
import type { ChessPuzzleData } from '../../../api/chess'
import { useTranslation } from '../../../i18n/LanguageContext'
import { useGameCompletion } from '../../../hooks/useGameCompletion'
import GamePlayHeader from '../../GamePlayHeader'
import SaveErrorBanner from '../../SaveErrorBanner'
import { COLORS } from '../../../styles/tokens'
import { Chess } from 'chess.js'

interface Props {
  difficulty:  ChessDifficulty
  onCompleted: (stats: { moves: number; startTime: number | null; endTime: number | null; pairsTotal: number; pairsMatched: number }) => void
  onBack:      () => void
}

const PUZZLES_PER_GAME = 5

type PuzzlePhase = 'user-move' | 'computer-move' | 'solved' | 'failed'

interface PuzzleState {
  puzzle:          ChessPuzzleData
  fen:             string           // current position
  solutionMoves:   string[]         // remaining UCI solution moves (user + computer alternating)
  playerColor:     Color            // which side the user plays
  selectedSquare:  Square | null
  validMoves:      Square[]
  lastFrom:        Square | null
  lastTo:          Square | null
  phase:           PuzzlePhase
  attempt:         boolean          // whether a wrong move was attempted
}

interface GameState {
  puzzleState:  PuzzleState | null
  loading:      boolean
  loadError:    string | null
  puzzleIdx:    number
  solved:       number
  totalMoves:   number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

function applySetupMove(puzzle: ChessPuzzleData): { fen: string; solutionMoves: string[]; playerColor: Color } {
  const moves      = puzzle.moves.split(' ')
  const [setup, ...solution] = moves
  const chess      = new Chess(puzzle.fen)
  const uci        = parseUci(setup)
  if (uci) chess.move({ from: uci.from, to: uci.to, promotion: 'q' })
  const fen        = chess.fen()
  const playerColor = sideToMove(fen)
  return { fen, solutionMoves: solution, playerColor }
}

function initGameState(): GameState {
  return {
    puzzleState:  null,
    loading:      true,
    loadError:    null,
    puzzleIdx:    0,
    solved:       0,
    totalMoves:   0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

export default function PuzzleMode({ difficulty, onCompleted, onBack }: Props) {
  const { t } = useTranslation()
  const ch    = t.chess
  const [state, setState] = useState<GameState>(initGameState)
  const [boardSize, setBoardSize] = useState(320)

  const handleBoardRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const update = () => {
      const sz = Math.min(el.clientWidth, el.clientHeight, 480)
      setBoardSize(Math.floor(sz / 8) * 8)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
  }, [])

  // Load next puzzle — setState only in async callbacks (no synchronous setState)
  const loadPuzzle = useCallback((_idx: number) => {
    chessApi.nextPuzzle(difficulty)
      .then((puzzle) => {
        const { fen, solutionMoves, playerColor } = applySetupMove(puzzle)
        setState((prev) => ({
          ...prev,
          loading: false,
          puzzleState: {
            puzzle,
            fen,
            solutionMoves,
            playerColor,
            selectedSquare: null,
            validMoves:     [],
            lastFrom:       null,
            lastTo:         null,
            phase:          'user-move',
            attempt:        false,
        },
        }))
      })
      .catch(() => {
        setState((prev) => ({ ...prev, loading: false, loadError: ch.puzzle.loadError }))
      })
  }, [difficulty, ch.puzzle.loadError])

  // Load first puzzle on mount
  useEffect(() => { loadPuzzle(0) }, [loadPuzzle])

  // Apply computer's response move after user solves their move
  useEffect(() => {
    if (!state.puzzleState || state.puzzleState.phase !== 'computer-move') return
    if (state.puzzleState.solutionMoves.length === 0) return

    const timer = setTimeout(() => {
      setState((prev) => {
        if (!prev.puzzleState || prev.puzzleState.phase !== 'computer-move') return prev
        const [computerUciStr, ...rest] = prev.puzzleState.solutionMoves
        const uci = parseUci(computerUciStr)
        if (!uci) return prev
        const newFen = applyMove(prev.puzzleState.fen, uci.from, uci.to)
        if (!newFen) return prev

        const nextPhase: PuzzlePhase = rest.length === 0 ? 'solved' : 'user-move'
        const solved = nextPhase === 'solved' ? prev.solved + 1 : prev.solved

        if (nextPhase === 'solved') {
          chessApi.recordAttempt(prev.puzzleState.puzzle.id, true).catch(() => {})
        }

        return {
          ...prev,
          puzzleState: {
            ...prev.puzzleState,
            fen:            newFen,
            solutionMoves:  rest,
            selectedSquare: null,
            validMoves:     [],
            lastFrom:       uci.from,
            lastTo:         uci.to,
            phase:          nextPhase,
          },
          solved,
        }
      })
    }, 600)
    return () => clearTimeout(timer)
  }, [state.puzzleState?.phase])  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSquareClick = useCallback((sq: Square) => {
    setState((prev) => {
      if (!prev.puzzleState || prev.puzzleState.phase !== 'user-move') return prev
      const ps = prev.puzzleState

      // If a piece is selected and sq is a valid move
      if (ps.selectedSquare && ps.validMoves.includes(sq)) {
        const correctUciStr = ps.solutionMoves[0]
        const correctUci    = parseUci(correctUciStr)
        const isCorrect     = correctUci && correctUci.from === ps.selectedSquare && correctUci.to === sq

        if (!isCorrect) {
          // Wrong move — record attempt, stay in same position
          chessApi.recordAttempt(ps.puzzle.id, false).catch(() => {})
          return {
            ...prev,
            totalMoves: prev.totalMoves + 1,
            puzzleState: { ...ps, selectedSquare: null, validMoves: [], attempt: true, phase: 'failed' },
          }
        }

        const newFen = applyMove(ps.fen, ps.selectedSquare, sq)
        if (!newFen) return prev

        const [, ...rest] = ps.solutionMoves
        const startTime   = prev.startTime ?? Date.now()
        // If no more moves after computer responds, it'll be solved in the computer-move effect
        const nextPhase: PuzzlePhase = rest.length === 0 ? 'solved' : 'computer-move'
        const solved = nextPhase === 'solved' ? prev.solved + 1 : prev.solved

        if (nextPhase === 'solved') {
          chessApi.recordAttempt(ps.puzzle.id, true).catch(() => {})
        }

        return {
          ...prev,
          totalMoves: prev.totalMoves + 1,
          startTime,
          solved,
          puzzleState: {
            ...ps,
            fen:           newFen,
            solutionMoves: rest,
            selectedSquare: null,
            validMoves:    [],
            lastFrom:      ps.selectedSquare,
            lastTo:        sq,
            phase:         nextPhase,
          },
        }
      }

      // Select a piece
      const validMoves = getValidMoveSquares(ps.fen, sq)
      if (validMoves.length > 0) {
        return { ...prev, puzzleState: { ...ps, selectedSquare: sq, validMoves } }
      }

      return { ...prev, puzzleState: { ...ps, selectedSquare: null, validMoves: [] } }
    })
  }, [])

  const nextPuzzle = useCallback(async () => {
    setState((prev) => {
      const nextIdx = prev.puzzleIdx + 1
      if (nextIdx >= PUZZLES_PER_GAME) {
        return { ...prev, gameComplete: true, endTime: Date.now() }
      }
      return { ...prev, puzzleIdx: nextIdx, loading: true, loadError: null, puzzleState: null }
    })
    // Load happens via effect below
  }, [])

  // Trigger load when puzzleIdx changes (and not already loading first puzzle)
  useEffect(() => {
    if (state.puzzleIdx > 0) loadPuzzle(state.puzzleIdx)
  }, [state.puzzleIdx]) // eslint-disable-line react-hooks/exhaustive-deps

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  state.gameComplete,
    activitySlug:  'chess',
    difficulty,
    mode:          'puzzle',
    moves:         state.totalMoves,
    startTime:     state.startTime,
    endTime:       state.endTime,
    pairsTotal:    PUZZLES_PER_GAME,
    pairsMatched:  state.solved,
    onCompleted:   () => onCompleted({
      moves:        state.totalMoves,
      startTime:    state.startTime,
      endTime:      state.endTime,
      pairsTotal:   PUZZLES_PER_GAME,
      pairsMatched: state.solved,
    }),
  })

  // Build highlights
  const highlights: Partial<Record<Square, SquareHighlight>> = {}
  if (state.puzzleState) {
    const ps = state.puzzleState
    if (ps.lastFrom) highlights[ps.lastFrom] = 'last-move'
    if (ps.lastTo)   highlights[ps.lastTo]   = 'last-move'
    if (ps.selectedSquare) {
      highlights[ps.selectedSquare] = 'selected'
      for (const sq of ps.validMoves) highlights[sq] = 'valid-move'
    }
  }

  const progress = (state.puzzleIdx / PUZZLES_PER_GAME) * 100
  const ps       = state.puzzleState

  if (state.loading) {
    return (
      <div style={s.root}>
        <GamePlayHeader title={ch.title} subtitle={ch.modes.puzzle.label}
          stats={[{ value: state.solved, label: ch.puzzle.solvedLabel }, { value: `${state.puzzleIdx + 1}/${PUZZLES_PER_GAME}`, label: ch.round }]}
          progress={progress} progressNow={state.puzzleIdx} progressMax={PUZZLES_PER_GAME}
          onBack={onBack} backLabel={ch.backToDashboard}
        />
        <div style={s.center}><p style={s.loadingText}>{ch.puzzle.loading}</p></div>
      </div>
    )
  }

  if (state.loadError || !ps) {
    return (
      <div style={s.root}>
        <GamePlayHeader title={ch.title} subtitle={ch.modes.puzzle.label}
          stats={[{ value: state.solved, label: ch.puzzle.solvedLabel }, { value: `${state.puzzleIdx + 1}/${PUZZLES_PER_GAME}`, label: ch.round }]}
          progress={progress} progressNow={state.puzzleIdx} progressMax={PUZZLES_PER_GAME}
          onBack={onBack} backLabel={ch.backToDashboard}
        />
        <div style={s.center}>
          <p style={s.loadingText}>{state.loadError}</p>
          <button style={{ ...s.btn, backgroundColor: COLORS.greenDark }} onClick={() => loadPuzzle(state.puzzleIdx)}>
            {ch.puzzle.retryBtn}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      <GamePlayHeader
        title={ch.title}
        subtitle={ch.modes.puzzle.label}
        stats={[
          { value: state.solved, label: ch.puzzle.solvedLabel },
          { value: `${state.puzzleIdx + 1}/${PUZZLES_PER_GAME}`, label: ch.round },
        ]}
        progress={progress}
        progressNow={state.puzzleIdx}
        progressMax={PUZZLES_PER_GAME}
        onBack={onBack}
        backLabel={ch.backToDashboard}
      />

      <div style={s.content}>
        <p style={s.instruction}>
          {ps.phase === 'solved' ? ch.puzzle.solved
            : ps.phase === 'failed' ? ch.puzzle.failed
            : ps.phase === 'computer-move' ? ch.puzzle.thinking
            : ch.puzzle.instruction.replace('{color}', ps.playerColor === 'w' ? ch.puzzle.white : ch.puzzle.black)}
        </p>

        <div ref={handleBoardRef} style={s.boardWrap}>
          <ChessBoard
            fen={ps.fen}
            size={boardSize}
            highlights={highlights}
            onSquareClick={ps.phase === 'user-move' ? handleSquareClick : undefined}
            disabled={ps.phase !== 'user-move'}
            flipped={ps.playerColor === 'b'}
          />
        </div>

        {(ps.phase === 'solved' || ps.phase === 'failed') && (
          <button style={{ ...s.btn, backgroundColor: ps.phase === 'solved' ? COLORS.greenDark : '#8e44ad' }} onClick={nextPuzzle}>
            {state.puzzleIdx + 1 >= PUZZLES_PER_GAME ? ch.puzzle.finishBtn : ch.puzzle.nextBtn}
          </button>
        )}
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root:         { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: COLORS.bgPage },
  content:      { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', overflowY: 'auto' },
  center:       { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '2rem' },
  instruction:  { fontSize: '1rem', color: '#333', fontStyle: 'italic', margin: 0, textAlign: 'center' },
  boardWrap:    { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' },
  loadingText:  { fontSize: '1.125rem', color: '#666', textAlign: 'center' },
  btn: {
    color: '#fff', border: 'none', borderRadius: 12, padding: '0.875rem 2rem',
    fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minHeight: 48,
  },
}
